import { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, PencilLine } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const emptyItem = () => ({
    _key: Math.random().toString(36).slice(2, 10),
    description: "",
    category: "",
    quantity: 1,
    unit: "u",
    unit_price: 0,
    total: 0,
    tags: [],
});

export default function AddDevisManualDialog({ projectId, onCreated, trigger }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [header, setHeader] = useState({
        contractor_name: "",
        trade: "",
        contractor_email: "",
        reference: "",
        tva_rate: 20,
        notes: "",
    });
    const [items, setItems] = useState([emptyItem()]);

    const updateItem = (idx, key, value) => {
        const next = items.map((it, i) =>
            i === idx ? { ...it, [key]: value } : it
        );
        if (key === "quantity" || key === "unit_price") {
            next[idx].total = parseFloat(
                (
                    parseFloat(next[idx].quantity || 0) *
                    parseFloat(next[idx].unit_price || 0)
                ).toFixed(2)
            );
        }
        setItems(next);
    };

    const submit = async () => {
        if (!header.contractor_name.trim() || !header.trade.trim()) {
            toast.error("Renseignez le nom de l'artisan et le corps de métier");
            return;
        }
        const cleanedItems = items
            .filter((it) => it.description.trim())
            .map((it) => ({
                ...it,
                quantity: parseFloat(it.quantity) || 0,
                unit_price: parseFloat(it.unit_price) || 0,
                total:
                    parseFloat(it.total) ||
                    parseFloat(it.quantity) * parseFloat(it.unit_price) ||
                    0,
                tags: it.tags || [],
            }));
        if (cleanedItems.length === 0) {
            toast.error("Ajoutez au moins un poste");
            return;
        }
        setLoading(true);
        try {
            const devis = await api.createDevis(projectId, {
                contractor_name: header.contractor_name.trim(),
                trade: header.trade.trim().toLowerCase(),
                contractor_email: header.contractor_email || null,
                reference: header.reference || null,
                tva_rate: parseFloat(header.tva_rate) || 20,
                notes: header.notes || null,
                line_items: cleanedItems,
            });
            toast.success("Devis créé");
            setOpen(false);
            setHeader({
                contractor_name: "",
                trade: "",
                contractor_email: "",
                reference: "",
                tva_rate: 20,
                notes: "",
            });
            setItems([emptyItem()]);
            onCreated && onCreated(devis);
        } catch (e) {
            toast.error("Erreur lors de la création");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {trigger || (
                    <Button
                        variant="outline"
                        data-testid="add-devis-manual-btn"
                        className="rounded-none border-ink hover:bg-ink hover:text-white"
                    >
                        <PencilLine
                            className="w-4 h-4 mr-2"
                            strokeWidth={1.6}
                        />
                        Saisir manuellement
                    </Button>
                )}
            </DialogTrigger>
            <DialogContent className="rounded-none border-ink max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">
                        Nouveau devis (saisie manuelle)
                    </DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label className="stat-label">
                            Entreprise / Artisan *
                        </Label>
                        <Input
                            data-testid="manual-contractor"
                            className="rounded-none mt-1.5"
                            value={header.contractor_name}
                            onChange={(e) =>
                                setHeader({
                                    ...header,
                                    contractor_name: e.target.value,
                                })
                            }
                        />
                    </div>
                    <div>
                        <Label className="stat-label">Corps de métier *</Label>
                        <Input
                            data-testid="manual-trade"
                            placeholder="plombier, électricien, terrassier…"
                            className="rounded-none mt-1.5"
                            value={header.trade}
                            onChange={(e) =>
                                setHeader({
                                    ...header,
                                    trade: e.target.value,
                                })
                            }
                        />
                    </div>
                    <div>
                        <Label className="stat-label">Email contact</Label>
                        <Input
                            className="rounded-none mt-1.5"
                            value={header.contractor_email}
                            onChange={(e) =>
                                setHeader({
                                    ...header,
                                    contractor_email: e.target.value,
                                })
                            }
                        />
                    </div>
                    <div>
                        <Label className="stat-label">N° devis</Label>
                        <Input
                            className="rounded-none mt-1.5"
                            value={header.reference}
                            onChange={(e) =>
                                setHeader({
                                    ...header,
                                    reference: e.target.value,
                                })
                            }
                        />
                    </div>
                    <div>
                        <Label className="stat-label">TVA (%)</Label>
                        <Input
                            type="number"
                            className="rounded-none mt-1.5 num"
                            value={header.tva_rate}
                            onChange={(e) =>
                                setHeader({
                                    ...header,
                                    tva_rate: e.target.value,
                                })
                            }
                        />
                    </div>
                </div>

                <div className="mt-2">
                    <div className="flex items-center justify-between mb-2">
                        <Label className="stat-label">Postes du devis</Label>
                        <Button
                            variant="outline"
                            size="sm"
                            className="rounded-none border-ink"
                            onClick={() => setItems([...items, emptyItem()])}
                            data-testid="add-line-item-btn"
                        >
                            <Plus className="w-3.5 h-3.5 mr-1" />
                            Ajouter une ligne
                        </Button>
                    </div>
                    <div className="border border-border">
                        <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-[#FAFAFA] border-b border-border text-[11px] uppercase tracking-wide text-ink-soft font-medium">
                            <div className="col-span-5">Désignation</div>
                            <div className="col-span-1 text-right">Qté</div>
                            <div className="col-span-1">Unité</div>
                            <div className="col-span-2 text-right">PU HT</div>
                            <div className="col-span-2 text-right">
                                Total HT
                            </div>
                            <div className="col-span-1"></div>
                        </div>
                        {items.map((it, idx) => (
                            <div
                                key={it._key || idx}
                                className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border last:border-0 items-center"
                            >
                                <Input
                                    className="rounded-none col-span-5 h-9"
                                    placeholder="Description du poste"
                                    value={it.description}
                                    onChange={(e) =>
                                        updateItem(
                                            idx,
                                            "description",
                                            e.target.value
                                        )
                                    }
                                    data-testid={`item-desc-${idx}`}
                                />
                                <Input
                                    type="number"
                                    className="rounded-none col-span-1 h-9 num text-right"
                                    value={it.quantity}
                                    onChange={(e) =>
                                        updateItem(
                                            idx,
                                            "quantity",
                                            e.target.value
                                        )
                                    }
                                />
                                <Input
                                    className="rounded-none col-span-1 h-9"
                                    value={it.unit}
                                    onChange={(e) =>
                                        updateItem(idx, "unit", e.target.value)
                                    }
                                />
                                <Input
                                    type="number"
                                    className="rounded-none col-span-2 h-9 num text-right"
                                    value={it.unit_price}
                                    onChange={(e) =>
                                        updateItem(
                                            idx,
                                            "unit_price",
                                            e.target.value
                                        )
                                    }
                                />
                                <div className="col-span-2 text-right num text-sm font-semibold">
                                    {it.total.toFixed(2)} €
                                </div>
                                <button
                                    className="col-span-1 text-ink-soft hover:text-danger"
                                    onClick={() =>
                                        setItems(
                                            items.filter((_, i) => i !== idx)
                                        )
                                    }
                                >
                                    <Trash2
                                        className="w-4 h-4"
                                        strokeWidth={1.6}
                                    />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <Label className="stat-label">Notes internes</Label>
                    <Textarea
                        className="rounded-none mt-1.5"
                        rows={2}
                        value={header.notes}
                        onChange={(e) =>
                            setHeader({ ...header, notes: e.target.value })
                        }
                    />
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        className="rounded-none"
                        onClick={() => setOpen(false)}
                    >
                        Annuler
                    </Button>
                    <Button
                        data-testid="manual-submit"
                        className="rounded-none bg-ink text-white hover:bg-ink/90"
                        onClick={submit}
                        disabled={loading}
                    >
                        {loading ? "Création…" : "Créer le devis"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
